import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Play, Check, AlertCircle, Copy, Eye, EyeOff, Key } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    CustomApiData,
    CustomApiAuth,
    KeyValuePair,
    useCreateCustomApiMutation,
    useUpdateCustomApiMutation,
    usePreviewCustomApiMutation,
} from '../../../features/integrations/customApiApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';



interface CustomApiFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: CustomApiData;
}

export const CustomApiFormModal: React.FC<CustomApiFormModalProps> = ({
    isOpen,
    onClose,
    initialData,
}) => {
    const isEditMode = !!initialData;

    // Form states
    const [name, setName] = useState('');
    const [apiUrl, setApiUrl] = useState('');
    const [apiType, setApiType] = useState<'weather' | 'prayer' | 'forex' | 'general'>('general');
    const [method, setMethod] = useState<'GET' | 'POST'>('GET');
    const [token, setToken] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [refreshInterval, setRefreshInterval] = useState(30);
    const [headers, setHeaders] = useState<KeyValuePair[]>([]);
    const [filtering, setFiltering] = useState<KeyValuePair[]>([]);
    const [requestBody, setRequestBody] = useState('');

    // Pre-fetch Auth States
    const [authEnabled, setAuthEnabled] = useState(false);
    const [authSectionOpen, setAuthSectionOpen] = useState(false);
    const [authUrl, setAuthUrl] = useState('');
    const [authMethod, setAuthMethod] = useState<'GET' | 'POST'>('POST');
    const [authPayload, setAuthPayload] = useState('');
    const [authTokenPath, setAuthTokenPath] = useState('');
    const [authHeaderKey, setAuthHeaderKey] = useState('');

    // Preview state
    const [previewData, setPreviewData] = useState<any>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);



    // API mutations
    const [createCustomApi, { isLoading: isCreating }] = useCreateCustomApiMutation();
    const [updateCustomApi, { isLoading: isUpdating }] = useUpdateCustomApiMutation();
    const [previewCustomApi] = usePreviewCustomApiMutation();

    // Populate form data on edit
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setName(initialData.name_custom || '');
                setApiUrl(initialData.api_url || '');
                setApiType(initialData.api_type || 'general');
                setMethod(initialData.method || 'GET');
                setToken(initialData.token || '');
                setRefreshInterval(initialData.refresh_interval || 30);
                let formattedHeaders: KeyValuePair[] = [];
                if (initialData.headers) {
                    if (Array.isArray(initialData.headers)) {
                        formattedHeaders = initialData.headers;
                    } else if (typeof initialData.headers === 'object') {
                        formattedHeaders = Object.entries(initialData.headers as unknown as Record<string, string>).map(
                            ([key, value]) => ({ key, value: String(value) })
                        );
                    }
                }
                setHeaders(formattedHeaders);

                let formattedFiltering: KeyValuePair[] = [];
                if (initialData.filtering) {
                    if (Array.isArray(initialData.filtering)) {
                        formattedFiltering = initialData.filtering;
                    } else if (typeof initialData.filtering === 'object') {
                        formattedFiltering = Object.entries(initialData.filtering as unknown as Record<string, string>).map(
                            ([key, value]) => ({ key, value: String(value) })
                        );
                    }
                }
                setFiltering(formattedFiltering);
                setRequestBody(
                    initialData.request_body
                        ? typeof initialData.request_body === 'string'
                            ? initialData.request_body
                            : JSON.stringify(initialData.request_body, null, 2)
                        : ''
                );
                if (initialData.auth) {
                    setAuthEnabled(initialData.auth.enabled || false);
                    setAuthSectionOpen(initialData.auth.enabled || false);
                    setAuthUrl(initialData.auth.auth_url || '');
                    setAuthMethod(initialData.auth.method || 'POST');
                    setAuthPayload(
                        initialData.auth.request_body
                            ? typeof initialData.auth.request_body === 'string'
                                ? initialData.auth.request_body
                                : JSON.stringify(initialData.auth.request_body, null, 2)
                            : ''
                    );
                    setAuthTokenPath(initialData.auth.token_path || '');
                    setAuthHeaderKey(initialData.auth.header_key || '');
                } else {
                    setAuthEnabled(false);
                    setAuthSectionOpen(false);
                    setAuthUrl('');
                    setAuthMethod('POST');
                    setAuthPayload('');
                    setAuthTokenPath('');
                    setAuthHeaderKey('');
                }
                setPreviewData(initialData.customapidata || null);
                setPreviewError(null);
            } else {
                // Reset form
                setName('');
                setApiUrl('');
                setApiType('general');
                setMethod('GET');
                setToken('');
                setRefreshInterval(30);
                setHeaders([]);
                setFiltering([]);
                setRequestBody('');
                setAuthEnabled(false);
                setAuthSectionOpen(false);
                setAuthUrl('');
                setAuthMethod('POST');
                setAuthPayload('');
                setAuthTokenPath('');
                setAuthHeaderKey('');
                setPreviewData(null);
                setPreviewError(null);
            }
        }
    }, [isOpen, initialData]);

    if (!isOpen) return null;

    // Helper functions for dynamic key-value rows
    const addHeaderRow = () => {
        setHeaders([...headers, { key: '', value: '' }]);
    };

    const updateHeaderRow = (index: number, field: 'key' | 'value', value: string) => {
        const newHeaders = [...headers];
        newHeaders[index][field] = value;
        setHeaders(newHeaders);
    };

    const removeHeaderRow = (index: number) => {
        setHeaders(headers.filter((_, i) => i !== index));
    };

    const addFilteringRow = () => {
        setFiltering([...filtering, { key: '', value: '' }]);
    };

    const updateFilteringRow = (index: number, field: 'key' | 'value', value: string) => {
        const newFiltering = [...filtering];
        newFiltering[index][field] = value;
        setFiltering(newFiltering);
    };

    const removeFilteringRow = (index: number) => {
        setFiltering(filtering.filter((_, i) => i !== index));
    };

    const handleTestApi = async () => {
        if (!apiUrl) {
            toast.error('Please enter an API URL');
            return;
        }

        if (authEnabled) {
            if (!authUrl.trim()) {
                toast.error('Please enter an Authentication URL');
                return;
            }
            if (!authTokenPath.trim()) {
                toast.error('Please enter a Token Extraction JSON Path');
                return;
            }
            if (!authHeaderKey.trim()) {
                toast.error('Please enter a Target Header Key');
                return;
            }
        }

        // Clean headers & filter out empty keys
        const cleanedHeaders = headers.filter(h => h.key.trim().length > 0);

        let parsedBody: any = null;
        if (method === 'POST' && requestBody.trim()) {
            try {
                parsedBody = JSON.parse(requestBody);
            } catch (err) {
                toast.error('Invalid JSON request body');
                return;
            }
        }

        let parsedAuthPayload: any = null;
        if (authEnabled && authPayload.trim()) {
            try {
                parsedAuthPayload = JSON.parse(authPayload);
            } catch (err) {
                toast.error('Invalid JSON authentication credentials payload');
                return;
            }
        }

        const authPayloadData: CustomApiAuth = authEnabled ? {
            enabled: true,
            auth_url: authUrl.trim() || null,
            method: authMethod as 'GET' | 'POST',
            request_body: parsedAuthPayload,
            token_path: authTokenPath.trim() || null,
            header_key: authHeaderKey.trim() || null,
        } : {
            enabled: false,
            auth_url: null,
            method: 'POST',
            request_body: null,
            token_path: null,
            header_key: null,
        };

        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewData(null);

        try {
            const response = await previewCustomApi({
                api_url: apiUrl,
                method,
                token: token.trim() || null,
                headers: cleanedHeaders,
                request_body: parsedBody,
                auth: authPayloadData,
            }).unwrap();

            if (response.success) {
                setPreviewData(response.data);
                toast.success('API preview fetched successfully!');
            } else {
                setPreviewError('Failed to fetch data');
            }
        } catch (err: any) {
            console.error('API Test Error:', err);
            const errMsg = err?.data?.message || err?.message || 'Error executing preview request';
            setPreviewError(errMsg);
            toast.error(errMsg);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error('Name is required');
            return;
        }
        if (!apiUrl.trim()) {
            toast.error('API URL is required');
            return;
        }

        if (authEnabled) {
            if (!authUrl.trim()) {
                toast.error('Please enter an Authentication URL');
                return;
            }
            if (!authTokenPath.trim()) {
                toast.error('Please enter a Token Extraction JSON Path');
                return;
            }
            if (!authHeaderKey.trim()) {
                toast.error('Please enter a Target Header Key');
                return;
            }
        }

        // Clean arrays by filtering out rows with empty keys
        const cleanedHeaders = headers.filter((h) => h.key.trim().length > 0);
        const cleanedFiltering = filtering.filter((f) => f.key.trim().length > 0);

        let parsedBody: any = null;
        if (method === 'POST' && requestBody.trim()) {
            try {
                parsedBody = JSON.parse(requestBody);
            } catch (err) {
                toast.error('Invalid JSON request body');
                return;
            }
        }

        let parsedAuthPayload: any = null;
        if (authEnabled && authPayload.trim()) {
            try {
                parsedAuthPayload = JSON.parse(authPayload);
            } catch (err) {
                toast.error('Invalid JSON authentication credentials payload');
                return;
            }
        }

        const authPayloadData: CustomApiAuth = authEnabled ? {
            enabled: true,
            auth_url: authUrl.trim() || null,
            method: authMethod as 'GET' | 'POST',
            request_body: parsedAuthPayload,
            token_path: authTokenPath.trim() || null,
            header_key: authHeaderKey.trim() || null,
        } : {
            enabled: false,
            auth_url: null,
            method: 'POST',
            request_body: null,
            token_path: null,
            header_key: null,
        };

        const payload: Partial<CustomApiData> = {
            name_custom: name.trim(),
            api_url: apiUrl.trim(),
            api_type: apiType,
            method,
            token: token.trim() || null,
            refresh_interval: refreshInterval,
            headers: cleanedHeaders,
            filtering: cleanedFiltering,
            request_body: parsedBody,
            customapidata: previewData, // save the tested preview data to local cache
            auth: authPayloadData,
        };

        try {
            if (isEditMode && initialData?._id) {
                await updateCustomApi({ id: initialData._id, data: payload }).unwrap();
                toast.success('Integration updated successfully');
            } else {
                await createCustomApi(payload).unwrap();
                toast.success('Integration created successfully');
            }
            onClose();
        } catch (err: any) {
            console.error('Save API Error:', err);
            const errMsg = err?.data?.message || err?.message || 'Failed to save integration';
            toast.error(errMsg);
        }
    };

    const copyToClipboard = () => {
        if (!previewData) return;
        navigator.clipboard.writeText(JSON.stringify(previewData, null, 2));
        toast.success('JSON copied to clipboard');
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-white rounded-[16px] shadow-2xl max-w-6xl w-full flex flex-col md:flex-row max-h-[90vh] overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
                {/* Left Side: Configuration Form */}
                <form
                    onSubmit={handleSubmit}
                    autoComplete="none"
                    className="flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar p-6 border-b md:border-b-0 md:border-r border-gray-200"
                >
                    {/* Bypass autofill by avoiding type="password" fields */}
                    <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-[#101928]">
                                {isEditMode ? 'Edit Custom API Integration' : 'New Custom API Integration'}
                            </h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Configure endpoint connection details and auto-refresh intervals.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-5">
                        {/* Name & Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Integration Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    autoComplete="off"
                                    placeholder="e.g. Dubai Airport Weather"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-3.5 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-sm font-sans"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Integration Context Type
                                </label>
                                <Select
                                    value={apiType}
                                    onValueChange={(val: string) => setApiType(val as any)}
                                >
                                    <SelectTrigger className="w-full h-[40px] px-3.5 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] bg-white transition-all text-sm font-sans [&>svg:last-child]:text-gray-400">
                                        <SelectValue placeholder="Select context..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-[8px] z-[99999]">
                                        <SelectItem value="general" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">General API</SelectItem>
                                        <SelectItem value="weather" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">Weather Template Binding</SelectItem>
                                        <SelectItem value="prayer" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">Prayer Times Binding</SelectItem>
                                        <SelectItem value="forex" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">Forex Rates Binding</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* URL & Method */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="md:col-span-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    HTTP Method
                                </label>
                                <Select
                                    value={method}
                                    onValueChange={(val: string) => setMethod(val as any)}
                                >
                                    <SelectTrigger className="w-full h-[40px] px-3.5 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] bg-white transition-all text-sm font-sans [&>svg:last-child]:text-gray-400">
                                        <SelectValue placeholder="Select method..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-[8px] z-[99999]">
                                        <SelectItem value="GET" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">GET</SelectItem>
                                        <SelectItem value="POST" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">POST</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-3">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    API URL <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    required
                                    autoComplete="off"
                                    placeholder="https://api.example.com/v1/weather"
                                    value={apiUrl}
                                    onChange={(e) => setApiUrl(e.target.value)}
                                    className="w-full px-3.5 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-sm font-mono"
                                />
                            </div>
                        </div>

                        {/* Refresh & Authorization */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Refresh Interval (Minutes)
                                </label>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    placeholder="30"
                                    value={refreshInterval || ''}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        setRefreshInterval(val ? parseInt(val) : 0);
                                    }}
                                    className="w-full px-3.5 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-sm font-sans"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Bearer Token / Secret (Optional)
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        autoComplete="off"
                                        placeholder="🔑 Enter API token or authentication secret"
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        style={{ WebkitTextSecurity: showToken ? 'none' : 'disc' } as React.CSSProperties}
                                        className="w-full pl-3.5 pr-10 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-sm font-sans"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowToken(!showToken)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        title={showToken ? "Hide Token" : "Show Token"}
                                    >
                                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Authentication Settings Card */}
                        <div className="border border-gray-200 rounded-[12px] bg-gray-50 overflow-hidden transition-all duration-200">
                            <button
                                type="button"
                                onClick={() => setAuthSectionOpen(!authSectionOpen)}
                                className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50/50 border-b border-gray-200/50 transition-colors cursor-pointer"
                            >
                                <div className="flex items-center gap-2.5">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${authEnabled ? 'bg-[#30A84B]/10 text-[#30A84B]' : 'bg-gray-100 text-gray-500'}`}>
                                        <Key className="w-4 h-4" />
                                    </div>
                                    <div className="text-left">
                                        <span className="block text-sm font-bold text-gray-800">Authentication Settings (Optional)</span>
                                        <span className="block text-xs text-gray-400">Enable automated login pre-fetch hook for session tokens</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {/* Custom styled toggle switch for authEnabled */}
                                    <div 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const nextEnabled = !authEnabled;
                                            setAuthEnabled(nextEnabled);
                                            if (nextEnabled) setAuthSectionOpen(true);
                                        }}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${authEnabled ? 'bg-[#30A84B]' : 'bg-gray-200'}`}
                                    >
                                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${authEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </div>
                                    <span className="text-gray-400 font-sans text-xs">
                                        {authSectionOpen ? 'Hide' : 'Show'}
                                    </span>
                                </div>
                            </button>
                            
                            {authSectionOpen && (
                                <div className="p-4 space-y-4 border-t border-gray-150 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-1">
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Auth HTTP Method
                                            </label>
                                            <Select
                                                value={authMethod}
                                                onValueChange={(val: string) => setAuthMethod(val as any)}
                                                disabled={!authEnabled}
                                            >
                                                <SelectTrigger className="w-full h-[38px] px-3 py-1.5 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] bg-white transition-all text-xs font-sans disabled:bg-gray-100 disabled:text-gray-400 [&>svg:last-child]:text-gray-400">
                                                    <SelectValue placeholder="POST" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-[8px] z-[99999]">
                                                    <SelectItem value="GET" className="text-xs py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">GET</SelectItem>
                                                    <SelectItem value="POST" className="text-xs py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">POST</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="md:col-span-3">
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Auth URL {authEnabled && <span className="text-red-500">*</span>}
                                            </label>
                                            <input
                                                type="url"
                                                required={authEnabled}
                                                disabled={!authEnabled}
                                                autoComplete="off"
                                                placeholder="https://api.hr-ms.com/login"
                                                value={authUrl}
                                                onChange={(e) => setAuthUrl(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-xs font-mono disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Extraction JSON Path {authEnabled && <span className="text-red-500">*</span>}
                                            </label>
                                            <input
                                                type="text"
                                                required={authEnabled}
                                                disabled={!authEnabled}
                                                autoComplete="off"
                                                placeholder="e.g. data.token or token"
                                                value={authTokenPath}
                                                onChange={(e) => setAuthTokenPath(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-xs font-mono disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Target Header Key {authEnabled && <span className="text-red-500">*</span>}
                                            </label>
                                            <input
                                                type="text"
                                                required={authEnabled}
                                                disabled={!authEnabled}
                                                autoComplete="off"
                                                placeholder="e.g. Sessiontoken or Authorization"
                                                value={authHeaderKey}
                                                onChange={(e) => setAuthHeaderKey(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] transition-all text-xs font-mono disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                                            Credentials Payload (JSON format)
                                        </label>
                                        <textarea
                                            rows={3}
                                            disabled={!authEnabled}
                                            placeholder={`{\n  "username": "...",\n  "password": "..."\n}`}
                                            value={authPayload}
                                            onChange={(e) => setAuthPayload(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] font-mono text-xs disabled:bg-gray-100 disabled:text-gray-400"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Headers Section */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-semibold text-gray-700">Headers</label>
                                <button
                                    type="button"
                                    onClick={addHeaderRow}
                                    className="text-xs text-[#30A84B] font-medium hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Add Header
                                </button>
                            </div>
                            {headers.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">No custom headers configured.</p>
                            ) : (
                                <div className="space-y-2">
                                    {headers.map((header, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Header Name"
                                                value={header.key}
                                                onChange={(e) => updateHeaderRow(index, 'key', e.target.value)}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-[6px] focus:outline-none text-xs font-mono"
                                            />
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Value"
                                                value={header.value}
                                                onChange={(e) => updateHeaderRow(index, 'value', e.target.value)}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-[6px] focus:outline-none text-xs font-mono"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeHeaderRow(index)}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* POST Request Body Section */}
                        {method === 'POST' && (
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Request Body (JSON format)
                                </label>
                                <textarea
                                    rows={4}
                                    placeholder="{\n  &quot;city&quot;: &quot;Dubai&quot;,\n  &quot;units&quot;: &quot;metric&quot;\n}"
                                    value={requestBody}
                                    onChange={(e) => setRequestBody(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#30A84B]/20 focus:border-[#30A84B] font-mono text-xs"
                                />
                            </div>
                        )}

                        {/* Query Filtering Params Section */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex flex-col">
                                    <label className="text-sm font-semibold text-gray-700">Filtering Query Parameters</label>
                                    <span className="text-[10px] text-gray-400">Maps keys to dynamic template filters</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={addFilteringRow}
                                    className="text-xs text-[#30A84B] font-medium hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Add Param
                                </button>
                            </div>
                            {filtering.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">No filtering query parameters configured.</p>
                            ) : (
                                <div className="space-y-2">
                                    {filtering.map((param, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Parameter Key (e.g. city)"
                                                value={param.key}
                                                onChange={(e) => updateFilteringRow(index, 'key', e.target.value)}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-[6px] focus:outline-none text-xs font-mono"
                                            />
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Fallback Value (e.g. Dubai)"
                                                value={param.value}
                                                onChange={(e) => updateFilteringRow(index, 'value', e.target.value)}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-[6px] focus:outline-none text-xs font-mono"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeFilteringRow(index)}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions footer */}
                    <div className="flex gap-3 justify-end items-center mt-8 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-[8px] hover:bg-gray-50 text-sm font-medium transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleTestApi}
                            disabled={previewLoading}
                            className="px-4 py-2 border border-[#30A84B] text-[#30A84B] rounded-[8px] hover:bg-[#30A84B]/5 text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                            {previewLoading ? (
                                <div className="w-4 h-4 border-2 border-[#30A84B] border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Play className="w-4 h-4" />
                            )}
                            Test & Preview
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            className="px-4 py-2 bg-[#30A84B] text-white rounded-[8px] hover:bg-[#288E3E] text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                            {isCreating || isUpdating ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Check className="w-4 h-4" />
                            )}
                            Save Integration
                        </button>
                    </div>
                </form>

                {/* Right Side: Preview Panel */}
                <div className="md:w-[420px] bg-slate-900 flex flex-col p-6 text-white min-h-[300px] md:min-h-0">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                        <h3 className="text-sm font-semibold tracking-wider text-slate-400 uppercase">
                            Outbound Live Response
                        </h3>
                        {previewData && (
                            <button
                                onClick={copyToClipboard}
                                className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                                title="Copy JSON"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="flex-1 relative overflow-hidden bg-slate-950 rounded-[8px] border border-slate-800 flex flex-col min-h-0">
                        {previewLoading ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                <div className="w-8 h-8 border-4 border-t-transparent border-[#30A84B] rounded-full animate-spin"></div>
                                <p className="text-xs text-slate-400">Querying remote server...</p>
                            </div>
                        ) : previewError ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-2">
                                <AlertCircle className="w-8 h-8 text-red-500" />
                                <p className="text-sm font-medium text-red-400">Request Failed</p>
                                <p className="text-xs text-slate-400 max-w-xs break-words">
                                    {previewError}
                                </p>
                            </div>
                        ) : previewData ? (
                            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-emerald-400 custom-scrollbar select-text leading-relaxed">
                                {JSON.stringify(previewData, null, 2)}
                            </pre>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-slate-500 gap-2">
                                <Play className="w-8 h-8 opacity-40 text-slate-400" />
                                <p className="text-sm font-semibold">Test Connection</p>
                                <p className="text-xs max-w-xs">
                                    Click "Test & Preview" on the left to verify your endpoint response payload structure.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-800 text-[10px] text-slate-400 flex flex-col gap-1.5">
                        <div className="flex justify-between">
                            <span>Integration Context:</span>
                            <span className="font-semibold text-slate-200 capitalize">{apiType}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Interval:</span>
                            <span className="font-semibold text-slate-200">Every {refreshInterval} minutes</span>
                        </div>
                        {initialData?.last_refreshed_at && (
                            <div className="flex justify-between">
                                <span>Last Cached Sync:</span>
                                <span className="font-semibold text-slate-200">
                                    {new Date(initialData.last_refreshed_at).toLocaleString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
